import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import { Button } from "./button";

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  compact?: boolean;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  actionLabel,
  actionHref,
  onAction,
  compact = false,
  className,
  ...props
}: EmptyStateProps) {
  // Render action button if actionLabel is provided
  const actionElement = action ?? (actionLabel ? (
    actionHref ? (
      <Button asChild size="sm">
        <Link to={actionHref}>{actionLabel}</Link>
      </Button>
    ) : onAction ? (
      <Button onClick={onAction} size="sm">
        {actionLabel}
      </Button>
    ) : null
  ) : null);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-md border border-dashed p-8 text-center animate-in fade-in-50",
        compact ? "min-h-[200px]" : "min-h-[400px]",
        className
      )}
      {...props}
    >
      {Icon && (
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Icon className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
      <h3 className="mt-4 text-lg font-semibold">{title}</h3>
      {description && (
        <p className="mt-2 mb-4 text-sm text-muted-foreground max-w-sm">
          {description}
        </p>
      )}
      {actionElement && <div className="mt-4">{actionElement}</div>}
    </div>
  );
}
